import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import {
  cleanDateFilters,
  DEFAULT_EMPTY_DATE_FILTERS,
  isValidDateRange,
} from "../utils/dateFilters";
import DateRangeInput from "./DateRangeInput";

const DateRangeFilter = ({
  value = DEFAULT_EMPTY_DATE_FILTERS,
  defaultValue = DEFAULT_EMPTY_DATE_FILTERS,
  onSearch,
  onReset,
  onDraftChange,
  fromKey = "encounterDateFrom",
  toKey = "encounterDateTo",
  showActions = true,
  showError = true,
  sx,
}) => {
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(value);
    setError("");
  }, [value]);

  const isDraftValid = (nextDraft) => {
    if (fromKey === "encounterDateFrom" && toKey === "encounterDateTo") {
      return isValidDateRange(nextDraft);
    }

    if (!nextDraft[fromKey] || !nextDraft[toKey]) return true;
    return nextDraft[fromKey] <= nextDraft[toKey];
  };

  const handleChange = (field) => (event) => {
    const next = {
      ...draft,
      [field]: event.target.value,
    };
    setDraft(next);
    onDraftChange?.(next);
    if (!isDraftValid(next)) {
      setError("From cannot be after To.");
    } else if (error) {
      setError("");
    }
  };

  const handleSearch = () => {
    if (!isDraftValid(draft)) {
      setError("From cannot be after To.");
      return;
    }

    setError("");
    onSearch?.(cleanDateFilters(draft));
  };

  const handleReset = () => {
    setError("");
    setDraft(defaultValue);
    onDraftChange?.(defaultValue);
    onReset?.(defaultValue);
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ mb: 2, ...sx }}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <DateRangeInput
        fromValue={draft[fromKey]}
        toValue={draft[toKey]}
        onFromChange={handleChange(fromKey)}
        onToChange={handleChange(toKey)}
        error={error}
      />
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ ml: { sm: "auto" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: "40px",
          }}
        >
          {showError && error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ lineHeight: 1.2, whiteSpace: "nowrap" }}
            >
              {error}
            </Typography>
          )}
        </Box>
        {showActions && (
          <>
            <Button
              variant="contained"
              onClick={handleSearch}
              size="small"
              sx={{ height: "40px", px: 3 }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              size="small"
              sx={{ height: "40px", px: 3 }}
            >
              Reset
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default DateRangeFilter;
